'use client';

import { useState } from 'react';
import { useRouter } from 'next/navigation';
import {
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Typography,
  Box,
  Avatar,
} from '@mui/material';
import LogoutIcon from '@mui/icons-material/Logout';
import SettingsIcon from '@mui/icons-material/Settings';
import Link from 'next/link';
import { useAuth } from '@boty/database';
import { useToast } from '@boty/ui';
import { useTranslations, useLocale } from 'next-intl';

/**
 * UserMenu - Dropdown menu for authenticated users
 *
 * Features:
 * - Displays user avatar with email initial
 * - Shows user email in dropdown
 * - Log out button that terminates session and redirects
 * - Accessible with proper ARIA attributes
 */
export function UserMenu() {
  const { user, signOut } = useAuth();
  const router = useRouter();
  const locale = useLocale();
  const t = useTranslations('creator.auth');
  const toast = useToast();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = async () => {
    handleMenuClose();

    const result = await signOut();

    if (result.error) {
      console.error('[UserMenu] Logout failed:', result.error);
      toast.error(t('logoutFailed'));
      return;
    }

    // Clear any local storage related to session
    localStorage.removeItem('boty_remember_session');
    localStorage.removeItem('pending_verification_email');

    // Redirect to quick-signup (passwordless auth entry point)
    router.push(`/${locale}/quick-signup`);
  };

  if (!user) return null;

  const userEmail = user.email || '';
  const userInitial = userEmail.charAt(0).toUpperCase();

  return (
    <>
      <IconButton
        onClick={handleMenuOpen}
        size="small"
        aria-label={t('myAccount')}
        aria-controls={anchorEl ? 'user-menu' : undefined}
        aria-haspopup="true"
        aria-expanded={anchorEl ? 'true' : undefined}
      >
        <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.main' }}>
          {userInitial}
        </Avatar>
      </IconButton>

      <Menu
        id="user-menu"
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        slotProps={{
          paper: {
            sx: { minWidth: 200 },
          },
        }}
      >
        <Box sx={{ px: 2, py: 1 }}>
          <Typography variant="body2" color="text.secondary" noWrap>
            {userEmail}
          </Typography>
        </Box>

        <Divider />

        <MenuItem
          component={Link}
          href={`/${locale}/account/settings`}
          onClick={handleMenuClose}
        >
          <ListItemIcon>
            <SettingsIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>{t('myAccount')}</ListItemText>
        </MenuItem>

        <MenuItem onClick={handleLogout}>
          <ListItemIcon>
            <LogoutIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>{t('logOut')}</ListItemText>
        </MenuItem>
      </Menu>
    </>
  );
}

export default UserMenu;
